/**
 * Marketing elements (progressive; the markup works without them).
 *
 *  <hero-slider data-interval data-autoplay>
 *    Cross-fading slides ([data-hero-slide]) selected by the rotation strip
 *    or progress bars ([data-hero-control]), previous/next arrows
 *    ([data-hero-prev], [data-hero-next]), swipe and arrow keys. Autoplay
 *    with a progress line ([data-hero-progress]) pauses off-screen, when the
 *    tab is hidden and for reduced motion; slide videos play only while
 *    active.
 *  <count-up data-count data-suffix data-delay>
 *    Counts a statistic up when it scrolls into view.
 *  <story-slider data-autoplay>
 *    Split story slider: scroll-snap track ([data-slider-track]) with
 *    previous/next, counter, progress line, keyboard arrows and autoplay
 *    that pauses on hover, focus and off-screen.
 *  <testimonial-slider data-autoplay>
 *    Quote stage: scroll-snap track ([data-slider-track]), previous/next,
 *    avatar buttons ([data-slider-to]), a progress line driven by the
 *    is-running class, live region, keyboard arrows; autoplay pauses on
 *    hover, focus, off-screen, hidden tab and reduced motion. RTL aware.
 *  <scrolling-marquee data-speed>
 *    Endless band: measures one copy of the list, adds copies until the
 *    loop covers the widest screen and sets the duration so the speed is
 *    constant (px per second) whatever the content width.
 *
 * Wrapped in a guard so the file may be included by several sections.
 */
if (!customElements.get('hero-slider')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.controls = Array.from(this.querySelectorAll('[data-hero-control]'));
      this.progress = this.querySelector('[data-hero-progress]');
      this.interval = Number(this.dataset.interval) || 7000;
      this.autoplay = this.dataset.autoplay === 'true';
      this.active = 0;
      this.timer = null;
      this.inView = true;

      this.controls.forEach((control) => {
        control.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(Number(control.dataset.heroControl));
          this.start();
        });
      });
      /* Classic style: round previous/next arrows on the stage. */
      [['[data-hero-prev]', -1], ['[data-hero-next]', 1]].forEach(([selector, step]) => {
        this.querySelector(selector)?.addEventListener('click', () => {
          this.select(this.active + step);
          this.start();
        });
      });

      let startX = null;
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') startX = event.clientX;
      });
      this.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) < 48) return;
        this.select(this.active + (delta < 0 ? 1 : -1));
        this.start();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        this.select(this.active + (event.key === 'ArrowRight' ? 1 : -1));
        this.start();
      });

      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      reducedMotion.addEventListener?.('change', () => (this.canPlay() ? this.start() : this.stop()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            if (this.inView) this.start();
            else this.stop();
            this.syncVideos();
          },
          { threshold: 0.2 }
        ).observe(this);
      }

      /* Pause button (snippets/motion-toggle) stops autoplay and films. */
      this.paused = false;
      this.addEventListener('motion:toggle', (event) => {
        this.paused = Boolean(event.detail?.paused);
        if (this.paused) this.stop();
        else this.start();
        this.syncVideos();
      });
      /* Theme editor: selecting a slide block shows that slide and holds it. */
      this.onBlockSelect = (event) => {
        const slide = event.target.closest?.('[data-hero-slide]');
        if (!slide || !this.contains(slide)) return;
        this.editing = true;
        this.select(Number(slide.dataset.heroSlide), false);
        this.stop();
      };
      this.onBlockDeselect = () => {
        this.editing = false;
        this.start();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
      this.select(0, false);
      this.start();
    }

    /* Media loading: a slide's image and film are attached only when it is
     * about to show (see snippets/hero-media), so the page downloads one
     * image and one film up front. Phones get their own cuts when set. */
    loadMedia(index) {
      const slide = this.slides[(index + this.slides.length) % this.slides.length];
      if (!slide) return;
      const phone = window.matchMedia('(max-width: 56.24rem)').matches;
      slide.querySelectorAll('img[data-src]').forEach((img) => {
        const src = (phone && img.dataset.srcMobile) || img.dataset.src;
        const srcset = (phone && img.dataset.srcsetMobile) || img.dataset.srcset || '';
        img.sizes = img.dataset.sizes || '100vw';
        if (srcset) img.srcset = srcset;
        img.src = src;
        img.removeAttribute('data-src');
      });
      const video = slide.querySelector('video[data-sources]');
      if (!video || this.saveData || reducedMotion.matches) return;
      let sources = [];
      try {
        sources = JSON.parse(video.dataset.sources);
      } catch {
        sources = [];
      }
      const wanted = phone ? 'phone' : 'desktop';
      const cap = phone ? 720 : 1080;
      let group = sources.filter((source) => source.for === wanted);
      if (!group.length) group = sources.filter((source) => source.for === 'desktop');
      const hls = group.filter((source) => /mpegurl/i.test(source.type || ''));
      const files = group.filter((source) => !/mpegurl/i.test(source.type || ''));
      const fitting = files.filter((source) => !source.h || source.h <= cap).sort((a, b) => b.h - a.h);
      const ordered = hls.concat(fitting.length ? fitting : files.sort((a, b) => a.h - b.h));
      ordered.forEach((source) => {
        const el = document.createElement('source');
        el.src = source.src;
        if (source.type) el.type = source.type;
        video.append(el);
      });
      video.removeAttribute('data-sources');
      video.preload = video.hasAttribute('data-eager') ? 'metadata' : 'auto';
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      video.load();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    canPlay() {
      return this.autoplay && this.slides.length > 1 && !reducedMotion.matches && this.inView && !document.hidden && !this.paused && !this.editing;
    }

    select(index, restartVideo = true) {
      if (!this.slides.length) return;
      this.active = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const on = i === this.active;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', String(!on));
        slide.toggleAttribute('inert', !on);
      });
      this.controls.forEach((control, i) => {
        const on = i === this.active;
        control.classList.toggle('is-active', on);
        if (on) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
      this.loadMedia(this.active);
      this.revealControl();
      this.syncVideos(restartVideo);
      /* The CSS entrance (hero--intro) belongs to the first paint only. */
      if (this.selectedOnce) this.classList.remove('hero--intro');
      this.selectedOnce = true;
    }

    /* Mobile strip: keep the active tab centred in the scroller. Scrolls the
     * strip only (scrollIntoView would also move the page to the strip). */
    revealControl() {
      const strip = this.querySelector('[data-hero-controls]');
      const control = this.controls[this.active];
      if (!strip || !control || strip.scrollWidth <= strip.clientWidth + 1) return;
      const stripBox = strip.getBoundingClientRect();
      const controlBox = control.getBoundingClientRect();
      const delta = controlBox.left + controlBox.width / 2 - (stripBox.left + stripBox.width / 2);
      strip.scrollBy({ left: delta, behavior: reducedMotion.matches || !this.selectedOnce ? 'auto' : 'smooth' });
    }

    syncVideos(restart = false) {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video || video.hasAttribute('data-sources')) return;
        const shouldPlay = i === this.active && !reducedMotion.matches && this.inView && !document.hidden && !this.paused;
        if (!shouldPlay) {
          video.pause();
          return;
        }
        if (restart) {
          try {
            video.currentTime = 0;
          } catch {
            /* plays from wherever it is once metadata arrives */
          }
        }
        video.play().catch(() => {});
      });
    }

    stop() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.prefetchTimer);
      this.timer = null;
      this.progress?.classList.remove('is-running');
    }

    start() {
      this.stop();
      if (!this.canPlay()) return;
      if (this.progress) {
        void this.progress.offsetWidth;
        this.progress.classList.add('is-running');
      }
      /* Attach the next slide's media shortly before it shows. */
      this.prefetchTimer = window.setTimeout(() => this.loadMedia(this.active + 1), Math.max(400, this.interval - 1800));
      this.timer = window.setTimeout(() => {
        this.select(this.active + 1);
        this.start();
      }, this.interval);
    }
  }

  class CountUp extends HTMLElement {
    connectedCallback() {
      const raw = String(this.dataset.count || '').replace(/[^\d.,-]/g, '').replace(/[.,](?=\d{3}\b)/g, '');
      const target = Number(raw.replace(',', '.'));
      if (!Number.isFinite(target) || reducedMotion.matches || !('IntersectionObserver' in window)) return;
      const suffix = this.dataset.suffix || '';
      const duration = Number(this.dataset.duration) || 1100;
      const decimals = (raw.split(/[.,]/)[1] || '').length;
      const format = (value) => value.toFixed(decimals) + suffix;
      this.textContent = format(0);
      const delay = Number(this.dataset.delay) || 0;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          const started = performance.now() + delay;
          const step = (now) => {
            const progress = Math.min(1, Math.max(0, (now - started) / duration));
            const eased = 1 - Math.pow(1 - progress, 4);
            this.textContent = format(target * eased);
            if (progress < 1) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        },
        { threshold: 0.5 }
      );
      observer.observe(this);
    }
  }

  class StorySlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.current = this.querySelector('[data-slider-current]');
      this.progress = this.querySelector('[data-slider-progress]');
      this.live = this.querySelector('[data-slider-live]');
      this.index = 0;
      this.visible = false;
      this.timer = 0;
      if (!this.track || this.slides.length < 2) {
        this.update(0);
        return;
      }
      this.querySelector('[data-slider-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.start(); });
      this.querySelector('[data-slider-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.start(); });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      });
      this.track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(() => {
          const centre = this.track.scrollLeft + this.track.clientWidth / 2;
          let best = 0;
          let distance = Infinity;
          this.slides.forEach((slide, i) => {
            const d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - centre);
            if (d < distance) { distance = d; best = i; }
          });
          if (best !== this.index) this.update(best);
        });
      }, { passive: true });
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('motion:toggle', (event) => {
        this.paused = Boolean(event.detail?.paused);
        if (this.paused) this.stop();
        else this.start();
      });
      bindEditorSelect(this);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.start();
          else this.stop();
        }, { threshold: 0.35 }).observe(this);
      } else {
        this.visible = true;
        this.start();
      }
      this.update(0);
    }

    disconnectedCallback() {
      this.stop();
      this.unbindEditor?.();
    }

    update(next) {
      this.index = Math.max(0, Math.min(this.slides.length - 1, next));
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
      if (this.current) this.current.textContent = String(this.index + 1).padStart(2, '0');
      if (this.live) this.live.textContent = `${this.index + 1} / ${this.slides.length}`;
      if (this.progress) this.progress.style.transform = `scaleX(${(this.index + 1) / this.slides.length})`;
    }

    /* Scroll the track only: scrollIntoView would also scroll the page
     * whenever the slider is partly off-screen during autoplay. */
    go(next) {
      const normalized = (next + this.slides.length) % this.slides.length;
      const slide = this.slides[normalized];
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const slideBox = slide.getBoundingClientRect();
      const trackBox = this.track.getBoundingClientRect();
      const delta = rtl ? slideBox.right - trackBox.right : slideBox.left - trackBox.left;
      this.track.scrollBy({ left: delta, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.update(normalized);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    start() {
      this.stop();
      const interval = Number(this.dataset.autoplay) || 0;
      if (!interval || reducedMotion.matches || !this.visible || this.paused || this.editing) return;
      this.timer = window.setInterval(() => this.go(this.index + 1), interval);
    }
  }

  /* Theme editor: selecting a slide block scrolls the slider to it and holds
   * autoplay until the block is deselected. */
  const bindEditorSelect = (slider) => {
    const onSelect = (event) => {
      const slide = event.target.closest?.('[data-slide]');
      if (!slide || !slider.contains(slide)) return;
      slider.editing = true;
      slider.stop();
      slider.go(slider.slides.indexOf(slide));
    };
    const onDeselect = () => {
      if (!slider.editing) return;
      slider.editing = false;
      slider.start();
    };
    document.addEventListener('shopify:block:select', onSelect);
    document.addEventListener('shopify:block:deselect', onDeselect);
    slider.unbindEditor = () => {
      document.removeEventListener('shopify:block:select', onSelect);
      document.removeEventListener('shopify:block:deselect', onDeselect);
    };
  };

  /* <recently-viewed data-url data-limit data-exclude>: fetches the cards for
   * the ids theme.js recorded, keeps the shopper's order, then reveals. */
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      let ids = [];
      try {
        ids = JSON.parse(localStorage.getItem('matter:recent') || '[]');
      } catch {
        ids = [];
      }
      ids = ids.filter((id) => id !== this.dataset.exclude).slice(0, Number(this.dataset.limit) || 8);
      const track = this.querySelector('[data-recently-viewed-track]');
      if (!ids.length || !track) return;
      const query = ids.map((id) => `id:${id}`).join(' OR ');
      const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&type=product&section_id=recently-viewed`;
      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = Array.from(doc.querySelectorAll('[data-recently-viewed-results] > li'));
          if (!items.length) return;
          items
            .sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId))
            .forEach((item) => track.append(item));
          this.hidden = false;
          this.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));
        })
        .catch(() => {});
    }
  }

  /* <hotspot-map>: shoppable image. One card open at a time; outside click
   * and Escape close; the theme editor opens the selected block's card. */
  class HotspotMap extends HTMLElement {
    connectedCallback() {
      this.spots = Array.from(this.querySelectorAll('[data-hotspot]'));
      this.spots.forEach((spot) => {
        spot.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggle(spot.dataset.hotspot);
        });
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeAll();
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-hotspot-card]')) return;
        if (!event.target.closest('[data-hotspot]')) this.closeAll();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });
      document.addEventListener('shopify:block:select', (event) => {
        const spot = event.target.closest?.('[data-hotspot]') || event.target.querySelector?.('[data-hotspot]');
        if (spot && this.contains(spot)) this.toggle(spot.dataset.hotspot, true);
      });
      document.addEventListener('shopify:block:deselect', () => this.closeAll());
    }

    toggle(id, force) {
      const spot = this.querySelector(`[data-hotspot="${id}"]`);
      const card = this.querySelector(`[data-hotspot-card="${id}"]`);
      if (!spot || !card) return;
      const open = force === true ? true : card.hidden;
      this.closeAll();
      if (!open) return;
      card.hidden = false;
      spot.classList.add('is-open');
      spot.setAttribute('aria-expanded', 'true');
    }

    closeAll() {
      this.querySelectorAll('[data-hotspot-card]').forEach((card) => {
        card.hidden = true;
      });
      this.spots.forEach((spot) => {
        spot.classList.remove('is-open');
        spot.setAttribute('aria-expanded', 'false');
      });
    }
  }

  /* <count-down data-end="YYYY-MM-DD HH:MM" data-expired>: ticks every
   * second; past the end it shows the expired text or hides. */
  class CountDown extends HTMLElement {
    connectedCallback() {
      /* Server-side timestamp (store time zone) first; the raw text is a
       * fallback read in the visitor's time zone. */
      const epoch = Number(this.dataset.endEpoch);
      const raw = (this.dataset.end || '').trim().replace(' ', 'T');
      this.end = Number.isFinite(epoch) && epoch > 0 ? epoch * 1000 : Date.parse(raw);
      if (!Number.isFinite(this.end)) {
        this.hidden = true;
        return;
      }
      this.fields = {
        days: this.querySelector('[data-count-days]'),
        hours: this.querySelector('[data-count-hours]'),
        minutes: this.querySelector('[data-count-minutes]'),
        seconds: this.querySelector('[data-count-seconds]'),
      };
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const left = this.end - Date.now();
      if (left <= 0) {
        window.clearInterval(this.timer);
        const expired = this.querySelector('.countdown__expired');
        const units = this.querySelector('.countdown__units');
        if (this.dataset.expired && expired) {
          expired.textContent = this.dataset.expired;
          expired.hidden = false;
          if (units) units.hidden = true;
          this.querySelector('.countdown__label')?.setAttribute('hidden', '');
        } else {
          this.hidden = true;
        }
        this.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true }));
        return;
      }
      const total = Math.floor(left / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const set = (key, value) => {
        if (this.fields[key] && this.fields[key].textContent !== value) this.fields[key].textContent = value;
      };
      set('days', String(Math.floor(total / 86400)));
      set('hours', pad(Math.floor((total % 86400) / 3600)));
      set('minutes', pad(Math.floor((total % 3600) / 60)));
      set('seconds', pad(total % 60));
    }
  }

  /* <image-compare style="--pos"> before/after slider driven by a range input. */
  class ImageCompare extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const apply = () => this.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', apply);
      apply();
    }
  }

  /* <promo-popup data-delay data-scroll data-frequency data-test>: opens
   * its dialog once per frequency window; the theme editor opens it when the
   * section is selected. */
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('dialog-drawer');
      if (!this.drawer) return;
      this.key = `matter:popup:${this.dataset.id}`;
      this.drawer.addEventListener('drawer:close', () => this.remember());
      this.querySelector('form')?.addEventListener('submit', () => this.remember());
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail?.sectionId === this.dataset.id) this.show(true);
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail?.sectionId === this.dataset.id) this.drawer.close();
      });
      if (window.Shopify?.designMode) return;
      if (this.dataset.enabled !== 'true' || !this.due()) return;
      if (this.dataset.trigger === 'scroll') {
        const target = Number(this.dataset.scroll) || 40;
        const check = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && (window.scrollY / max) * 100 >= target) {
            window.removeEventListener('scroll', check);
            this.show();
          }
        };
        window.addEventListener('scroll', check, { passive: true });
      } else {
        window.setTimeout(() => this.show(), (Number(this.dataset.delay) || 6) * 1000);
      }
    }

    due() {
      if (this.dataset.test === 'true') return true;
      try {
        const last = Number(localStorage.getItem(this.key) || 0);
        const days = Number(this.dataset.frequency) || 7;
        return Date.now() - last > days * 86400000;
      } catch {
        return true;
      }
    }

    remember() {
      try {
        localStorage.setItem(this.key, String(Date.now()));
      } catch { /* private mode */ }
    }

    show(force) {
      if (!force && document.querySelector('dialog[open]')) return;
      this.drawer.open();
    }
  }

  class TestimonialSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.isStack = this.classList.contains('testimonials--stage');
      this.tabs = Array.from(this.querySelectorAll('[data-slider-to]'));
      this.live = this.querySelector('[data-slider-live]');
      this.counter = this.querySelector('[data-slider-count]');
      this.interval = Number(this.dataset.autoplay) || 0;
      this.index = 0;
      this.visible = false;
      this.timer = 0;
      this.style.setProperty('--interval', `${this.interval}ms`);
      if (!this.track || this.slides.length < 2) {
        this.update(0);
        return;
      }
      this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.go(Number(tab.dataset.sliderTo))));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      });
      if (this.isStack) {
        this.track.addEventListener('pointerdown', (event) => {
          this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
        });
        this.track.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
          const dx = event.clientX - this.pointerStart.x;
          const dy = event.clientY - this.pointerStart.y;
          this.pointerStart = null;
          if (Math.abs(dx) < 55 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
          this.swiped = true;
          this.go(this.index + (dx < 0 ? 1 : -1));
          window.setTimeout(() => { this.swiped = false; }, 100);
        });
        this.track.addEventListener('pointercancel', () => { this.pointerStart = null; });
        this.track.addEventListener('click', (event) => {
          if (!this.swiped) return;
          event.preventDefault();
          this.swiped = false;
        }, true);
      } else {
        /* The alternate card rail keeps native scroll-snap and swipe sync. */
        this.track.addEventListener('scrollend', () => { this.settling = false; });
        this.track.addEventListener('scroll', () => {
          if (this.settling) return;
          window.cancelAnimationFrame(this.frame);
          this.frame = window.requestAnimationFrame(() => {
            const { scrollWidth, clientWidth, scrollLeft } = this.track;
            const start = this.rtl ? scrollWidth + scrollLeft - clientWidth : scrollLeft;
            const centre = start + clientWidth / 2;
            let best = 0;
            let distance = Infinity;
            this.slides.forEach((slide, i) => {
              const d = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - centre);
              if (d < distance) { distance = d; best = i; }
            });
            if (best !== this.index) this.update(best);
          });
        }, { passive: true });
      }
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('motion:toggle', (event) => {
        this.paused = Boolean(event.detail?.paused);
        if (this.paused) this.stop();
        else this.start();
      });
      bindEditorSelect(this);
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.start();
          else this.stop();
        }, { threshold: 0.35 }).observe(this);
      } else {
        this.visible = true;
        this.start();
      }
      this.update(0);
    }

    disconnectedCallback() {
      this.stop();
      window.clearTimeout(this.exitTimer);
      this.unbindEditor?.();
    }

    get rtl() {
      return getComputedStyle(this.track).direction === 'rtl';
    }

    /* Scroll the track so slide i sits at the start edge; in RTL the start
     * edge is on the right and scrollLeft runs negative. */
    go(next) {
      const count = this.slides.length;
      const i = ((next % count) + count) % count;
      if (this.isStack) {
        this.update(i, true, next === this.index - 1 ? 'previous' : 'next');
        this.start();
        return;
      }
      const slide = this.slides[i];
      const { scrollWidth } = this.track;
      const left = this.rtl ? slide.offsetLeft + slide.offsetWidth - scrollWidth : slide.offsetLeft;
      this.settling = true;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => { this.settling = false; }, 900);
      this.track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.update(i);
      this.start();
    }

    update(next, animate = false, direction = 'next') {
      const previous = this.index;
      this.index = Math.max(0, Math.min(this.slides.length - 1, next));
      if (this.isStack) {
        window.clearTimeout(this.exitTimer);
        this.exitSlide = null;
      }
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (!this.isStack) return;
        const depth = (i - this.index + this.slides.length) % this.slides.length;
        const leaving = animate && !reducedMotion.matches && previous !== this.index && i === previous;
        slide.dataset.stackDepth = leaving ? 'exit' : depth < 3 ? String(depth) : 'hidden';
        if (leaving) slide.dataset.stackExitDirection = direction;
        else delete slide.dataset.stackExitDirection;
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        if (leaving) this.exitSlide = slide;
      });
      if (this.exitSlide) {
        const leaving = this.exitSlide;
        this.exitTimer = window.setTimeout(() => {
          const depth = (this.slides.indexOf(leaving) - this.index + this.slides.length) % this.slides.length;
          leaving.dataset.stackDepth = depth < 3 ? String(depth) : 'hidden';
          delete leaving.dataset.stackExitDirection;
          if (this.exitSlide === leaving) this.exitSlide = null;
        }, 430);
      }
      this.tabs.forEach((tab, i) => {
        if (i === this.index) tab.setAttribute('aria-current', 'true');
        else tab.removeAttribute('aria-current');
      });
      if (this.live) this.live.textContent = this.slides[this.index]?.getAttribute('aria-label') || '';
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    }

    start() {
      this.stop();
      if (!this.interval || reducedMotion.matches || !this.visible || document.hidden || this.paused || this.editing) return;
      this.classList.add('is-running');
      this.timer = window.setTimeout(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.classList.remove('is-running');
    }
  }

  class ScrollingMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      const copies = Array.from(this.querySelectorAll('[data-marquee-copy]'));
      this.template = copies[1] || copies[0];
      if (!this.track || !this.template) return;
      this.speed = Number(this.dataset.speed) || 5;
      this.fit = this.fit.bind(this);
      if ('ResizeObserver' in window) new ResizeObserver(() => this.queue()).observe(this);
      else window.addEventListener('resize', () => this.queue());
      document.fonts?.ready?.then(() => this.queue());
      this.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', () => this.queue(), { once: true });
      });
      this.queue();
    }

    queue() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(this.fit);
    }

    /* One copy must cover the viewport plus itself so the loop never shows a
     * gap; the duration follows the copy width for a constant px/s. */
    fit() {
      const width = this.template.getBoundingClientRect().width;
      if (!width) return;
      const needed = Math.max(4, Math.ceil(this.clientWidth / width) + 2);
      let copies = this.track.querySelectorAll('[data-marquee-copy]').length;
      while (copies < needed) {
        const clone = this.template.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        this.track.append(clone);
        copies += 1;
      }
      this.track.style.setProperty('--marquee-copies', String(copies));
      this.style.setProperty('--marquee-duration', `${(width / (this.speed * 12)).toFixed(2)}s`);
    }
  }

  customElements.define('hero-slider', HeroSlider);
  customElements.define('testimonial-slider', TestimonialSlider);
  customElements.define('scrolling-marquee', ScrollingMarquee);
  customElements.define('count-down', CountDown);
  customElements.define('image-compare', ImageCompare);
  customElements.define('promo-popup', PromoPopup);
  customElements.define('recently-viewed', RecentlyViewed);
  customElements.define('hotspot-map', HotspotMap);
  customElements.define('count-up', CountUp);
  customElements.define('story-slider', StorySlider);
}
